import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fetchGuestByEndpoint } from '../lib/guestApi';

// Import components
import Hero from '../components/Hero';
import GroomBride from '../components/GroomBride';
import InfoSection from '../components/InfoSection';
import RouteSection from '../components/RouteSection';
import WeatherSection from '../components/WeatherSection';
import RsvpSection from '../components/RsvpSection';
import Footer from '../components/Footer';
import InviteError from './InviteError';

import './InvitePage.css';

const InvitePage = ({
  labelField = 'label',
  rpcName = 'get_guest_by_endpoint',
  showRsvp = true,
  showRouteSection = false,
  ceremonyDescription,
  receptionTimeline,
  receptionDirectionsButtonLabel,
  receptionNote = 'Okupljanje, potom večera, ples i dobra zabava!',
  receptionParking,
  receptionDietaryNote,
  showWeatherSection = false,
  footerContacts,
}) => {
  const { endpoint } = useParams();
  const [loading, setLoading] = useState(true);
  const [guest, setGuest] = useState(null);
  const [errorType, setErrorType] = useState(null); // null | "not_found" | "server"

  const loadGuest = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setErrorType(null);

    try {
      const guestData = await fetchGuestByEndpoint(endpoint, { rpcName });

      if (!guestData) {
        setErrorType('not_found');
      } else {
        setGuest(guestData);
      }
    } catch (error) {
      console.error('Error fetching guest data:', error);
      setErrorType('server');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [endpoint, rpcName]);

  useEffect(() => {
    loadGuest();
  }, [loadGuest]);

  // Refetch guest data after RSVP update
  const handleRsvpUpdated = useCallback(() => {
    loadGuest(false);
  }, [loadGuest]);

  // Loading state
  if (loading) {
    return (
      <div className="invite-loading">
        <div className="invite-loading__spinner" aria-hidden="true"></div>
        <p className="invite-loading__text">Učitavam vašu pozivnicu...</p>
      </div>
    );
  }

  // Error states
  if (errorType === 'not_found') {
    return <InviteError variant="not_found" />;
  }

  if (errorType === 'server') {
    return <InviteError variant="server" />;
  }

  const guestLabel = guest[labelField];

  // Success: render personalized invitation
  return (
    <div className="wedding-app">
      {/* Hero Section with personalized guest label */}
      <Hero guestLabel={guestLabel} />

      {/* Groom & Bride Section */}
      <GroomBride guestLabel={guestLabel} showRsvp={showRsvp} />

      {/* Ceremony Section */}
      <InfoSection
        type="ceremony"
        time="17:00 h"
        venue="Crkva sv. Nikole Biskupa"
        address="Medpotoki ul. 1, Zagreb"
        mapsUrl="https://maps.app.goo.gl/F7ia6YPpAGZjyZXq9"
        description={ceremonyDescription}
      />

      {/* Route Section (Ceremony -> Reception) */}
      {showRouteSection && (
        <RouteSection
          origin="Medpotoki ul. 1, Zagreb"
          destination="Dubravkin put 2, Zagreb"
          mapsUrl="https://www.google.com/maps/dir/Sveti+Nikola+biskup,+Medpotoki+ul.+1,+10000,+Zagreb/45.8241806,15.934413/45.8427934,15.9633865/Dubravkin+put,+2,+10000,+Zagreb/@45.8295434,15.9202813,9018m/data=!3m1!1e3!4m21!4m20!1m5!1m1!1s0x4765d1a84741a9b3:0xb9359b1d5a458996!2m2!1d15.8804034!2d45.8198855!1m5!3m4!1m2!1d15.9307062!2d45.8491903!3s0x4765d0bc959fcda3:0x8e7059a3950e0cfe!1m0!1m5!1m1!1s0x4765d71cc1b92995:0x4f1bb0529c138286!2m2!1d15.9732894!2d45.818533!3e0?entry=ttu&g_ep=EgoyMDI2MDUyNy4wIKXMDSoASAFQAw%3D%3D"
          embedSrc="https://www.google.com/maps/embed?pb=!1m36!1m12!1m3!1d31456.995598653473!2d15.920281315073884!3d45.82954343515034!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m21!3e0!4m5!1s0x4765d1a84741a9b3%3A0xb9359b1d5a458996!2sSveti%20Nikola%20biskup%2C%20Medpotoki%20ul.%201%2C%2010000%2C%20Zagreb!3m2!1d45.8198855!2d15.880403399999999!4m3!3m2!1d45.8241806!2d15.934413!4m3!3m2!1d45.8427934!2d15.963386499999999!4m5!1s0x4765d71cc1b92995%3A0x4f1bb0529c138286!2sDubravkin%20put%2C%202%2C%2010000%2C%20Zagreb!3m2!1d45.818532999999995!2d15.973289399999999!5e1!3m2!1sen!2shr!4v1780221580977!5m2!1sen!2shr"
        />
      )}

      {/* Reception Section */}
      <InfoSection
        type="reception"
        time="19:00 h"
        venue="Restoran Dubravkin put"
        address="Dubravkin put 2, Zagreb"
        note={receptionNote}
        mapsUrl="https://maps.app.goo.gl/3tMpXQsoRnSScm5a6"
        timeline={receptionTimeline}
        directionsButtonLabel={receptionDirectionsButtonLabel}
        parking={receptionParking}
        dietaryNote={receptionDietaryNote}
      />

      {/* Weather Section */}
      {showWeatherSection && <WeatherSection />}

      {/* RSVP Section */}
      {showRsvp && (
        <RsvpSection
          endpoint={endpoint}
          guest={guest}
          onUpdated={handleRsvpUpdated}
        />
      )}

      {/* Footer */}
      <Footer contacts={footerContacts} />
    </div>
  );
};

export default InvitePage;
